import React, { useMemo, useState } from 'react';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import LaboratoryRequestCreateRequestHeaderForm, {
	LaboratoryRequestCreateDocumentInfo,
	LaboratoryRequestCreatePatient,
	LaboratoryRequestCreateRequestHeaderFormValues,
} from './LaboratoryRequestCreateRequestHeaderForm';
import {
	LaboratoryDataInput,
	useLaboratoryRequestCreateMutation,
} from '../../../../graphql/graphql';
import useNotification from '../../../../Hooks/useNotification.hook';
import { useAppContext } from '../../../../Services/AppContext';
import useTranslation from '../../../../Hooks/useTranslation';
import LaboratoryRequestCreateForm, {
	LaboratoryRequestFormDataScheme,
} from '../LaboratoryRequestCreate/LaboratoryRequestCreateForm';
import LaboratoryRequestCreateRecapitulation from './LaboratoryRequestCreateRecapitulation';
import LaboratoryRequestCreateWizardStepWrapper from './LaboratoryRequestCreateWizardStepWrapper';
import useFilters from '../../../Base/Hooks/useFilters';
import OverlayLoading from '../../../../Components/OverlayLoading';

interface Props {
	onCreate: () => void;
	formId: string;
	patientId: string;
}

interface DataScheme {
	patient?: LaboratoryRequestCreatePatient;
	documentInfo?: LaboratoryRequestCreateDocumentInfo;
	data?: LaboratoryRequestFormDataScheme[];
	saveToECard?: boolean;
}

const LaboratoryRequestCreateWizard: React.FC<Props> = ({
	formId,
	patientId,
	onCreate,
}) => {
	const [createRequest] = useLaboratoryRequestCreateMutation();
	const { showNotificationSuccess } = useNotification();
	const { workplaceId } = useAppContext();
	const { t } = useTranslation();
	const [data, setData] = useState<DataScheme>({});
	const { handleDateValue, handleTimeValue, checkEmptyString, handleSelect } =
		useFilters();
	const [isLoading, setIsLoading] = useState(false);

	const handleSendRequest = async (data: DataScheme) => {
		setIsLoading(true);
		console.log('data.documentInfo?.tookBy', data.documentInfo?.tookBy);
		if (data.patient && data.data && data.documentInfo) {
			const response = await createRequest({
				input: {
					requisitionId: formId,
					userId: patientId,
					workplaceId: workplaceId,
					document: {
						date: handleDateValue(data.documentInfo?.date),
						time: handleTimeValue(data.documentInfo?.time),
						primarySampleType: data.documentInfo?.primarySampleType,
						selfPaid: data.documentInfo?.selfPaid,
						statim: data.documentInfo?.statim,
						statimPhoneNumber: data.documentInfo?.statimPhoneNumber,
						sampleTakenBy: checkEmptyString(
							data.documentInfo?.tookBy?.label ??
								data.documentInfo?.tookByCustom,
						),
					},
					patient: {
						diagnosisTermItemValue: handleSelect(data.patient.diagnosis) ?? '',
						genderTermItemId: handleSelect(data.patient?.gender) ?? '',
						insuranceCompanyTermItemId:
							handleSelect(data.patient?.insuranceCompany) ?? '',
						address: data.patient?.address,
						birthDate: handleDateValue(data.patient?.birthDate),
						insuranceNumber: data?.patient?.insuranceNumber,
						lastName: data?.patient?.lastName,
						firstName: data?.patient?.firstName,
					},
					data: data.data?.map(item => {
						return {
							wizardItemId: item.wizardItemId,
							value: item.value,
						} as LaboratoryDataInput;
					}),
					addToPatientECard: data.saveToECard ?? false,
				},
			}).unwrap();
			if (response.saveLaboratoryRequisitionResult?.id) {
				showNotificationSuccess({
					content: t('component.laboratoryRequestCreate.requestCreated'),
				});
				onCreate();
			}
		}
		setIsLoading(false);
	};

	const handleOnPatientDataSuccess = (
		params: NavigationStepperComponentParams,
		values: LaboratoryRequestCreateRequestHeaderFormValues,
	) => {
		setData({
			patient: values.patient,
			documentInfo: values.documentInfo,
			data: data?.data,
		});
		params.setActiveStepPath('requestData');
	};

	const handleOnRequestDataSuccess = (
		params: NavigationStepperComponentParams,
		requestData: LaboratoryRequestFormDataScheme[],
	) => {
		setData({
			patient: data?.patient,
			documentInfo: data?.documentInfo,
			data: requestData,
		});
		params.setActiveStepPath('recapitulation');
	};

	const handleConfirm = async (saveToECard: boolean) => {
		await handleSendRequest({ ...data, saveToECard });
	};

	const steps: NavigationStepperItemType[] = useMemo(() => {
		return [
			{
				name: 'component.laboratoryRequestCreateWizard.patientData',
				key: 'patientData',
				component: (params: NavigationStepperComponentParams) => (
					<LaboratoryRequestCreateWizardStepWrapper>
						<LaboratoryRequestCreateRequestHeaderForm
							onSuccess={values => handleOnPatientDataSuccess(params, values)}
							patientId={patientId}
						/>
					</LaboratoryRequestCreateWizardStepWrapper>
				),
			},
			{
				name: 'component.laboratoryRequestCreateWizard.requestData',
				key: 'requestData',
				component: (params: NavigationStepperComponentParams) => (
					<LaboratoryRequestCreateWizardStepWrapper>
						<LaboratoryRequestCreateForm
							onSuccess={values => handleOnRequestDataSuccess(params, values)}
							formId={formId}
						/>
					</LaboratoryRequestCreateWizardStepWrapper>
				),
			},
			{
				name: 'component.laboratoryRequestCreateWizard.recapitulation',
				key: 'recapitulation',
				component: (params: NavigationStepperComponentParams) => (
					<LaboratoryRequestCreateWizardStepWrapper>
						<LaboratoryRequestCreateRecapitulation
							onConfirm={handleConfirm}
							data={data}
							patientId={patientId}
						/>
					</LaboratoryRequestCreateWizardStepWrapper>
				),
			},
		];
	}, [data]);

	return (
		<>
			<NavigationStepper
				steps={steps}
				initialStepPath='patientData'
				canSkip={false}
			/>
			{isLoading && <OverlayLoading />}
		</>
	);
};

export default LaboratoryRequestCreateWizard;
